import React, {useEffect, useState} from "react";

import {Button, Checkbox, DatePicker, Form, InputNumber, message, Select, Spin, Tag} from "antd";
import TextArea from "antd/es/input/TextArea";
import dayjs from "dayjs";

import {TasksViewer} from "./TasksViewer";
import api from "../../api";
import {checkAllPermissions, checkAnyOfPermissions} from "../../helpers";
import {ROP_MIN_LABEL} from "../../properties";
import {DataCard} from "../helpers/cards/Card";
import {ModalWindow} from "../helpers/modals/ModalWindow";
import {extractErrorMessage} from "../../index";

const REVIEW_ROLES = ["senior-user", "approve-user", "superadmin"];

export const AssignApproval = ({
	type = "material",
	projectId,
	setTaskId,
	itemInfo,
	currentTaskId,
	scope,
	shouldSelectWorkflow = true,
	closeApproval,
	onApprove,
	canEdit,
	show = false,
	proposedMin = 0,
	proposedMax = 0,
	approvalStatus,
}) => {
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [isAdmin, setIsAdmin] = useState(false);
	const [canReview, setCanReview] = useState(false);
	const [isReviewModalVisible, setIsReviewModalVisible] = useState(false);
	const [isResetModalVisible, setIsResetModalVisible] = useState(false);
	const [workflows, setWorkflows] = useState([]);
	const [tasks, setTasks] = useState([]);
	const [loading, setLoading] = useState(false);
	const [userTasks, setUserTasks] = useState([]);
	const [scopeMode, setScopeMode] = useState("scenario");
	const [scenarios, setScenarios] = useState([]);
	const [msg, setMsg] = useState("");
	const [allowSelect, setAllowSelect] = useState(shouldSelectWorkflow);

	const [user, setUser] = useState({});
	const [form] = Form.useForm();

	const resetFormState = ({prefillMinMax = false} = {}) => {
		form.resetFields();
		if (prefillMinMax) {
			form.setFieldsValue({
				min: proposedMin,
				max: proposedMax,
			});
		}
		setMsg("");
		setAllowSelect(shouldSelectWorkflow);
		setLoading(false);
	};

	const closeModal = () => {
		resetFormState();
		closeApproval();
		setIsModalVisible(false);
	};

	const getScenarios = () => {
		const data = {
			pagination: {
				current: null,
			},
			_filters: {
				scope: [scope],
			},
		};

		if (projectId && scope) {
			api.post(`/projects/${projectId}/iterations`, data).then(json => {
				setScenarios(json.data.iterations);
			});
		}
	};

	useEffect(() => {
		if (show) {
			resetFormState({prefillMinMax: true});
			setIsModalVisible(true);
		}
	}, [show]);

	const openModal = () => {
		resetFormState();
		setIsModalVisible(true);
	};

	const checkIfAdmin = () => {
		checkAllPermissions(["admin"])
			.then(() => setIsAdmin(true))
			.catch(() => setIsAdmin(false));
	};

	const checkIfCanReview = () => {
		checkAnyOfPermissions(REVIEW_ROLES)
			.then(() => setCanReview(true))
			.catch(() => setCanReview(false));
	};

	useEffect(() => {
		shouldSelectWorkflow && getWorkflows();
		getTasks();
		setMsg("");
		getUserTasks();
	}, [itemInfo, isModalVisible]);

	useEffect(() => {
		getUser();
		getUserTasks();
		checkIfAdmin();
		checkIfCanReview();
	}, []);

	useEffect(() => {
		setAllowSelect(shouldSelectWorkflow);
		setMsg("");
	}, [shouldSelectWorkflow]);

	useEffect(() => {
		if (type === "scope") {
			getScenarios();
		}
	}, [type, scope]);

	useEffect(() => {
		getUserTasks();
	}, [user]);

	const getWorkflows = () => {
		api.get(`/projects/${projectId}/workflows`).then(json => setWorkflows(json.data.data));
	};

	const getTasks = () => {
		api.get(`/projects/${projectId}/tasks/object_id/${itemInfo["material"]}/object_type/${type}`).then(json => {
			setTasks(json.data);
			if (setTaskId && json.data.length > 0) {
				if (currentTaskId !== json.data[0].task_id) {
					setTaskId(json.data[0].task_id);
				}
			}
			approvalStatus(json.data.filter(v => v.status !== "done").length > 0);
		});
	};

	const getUser = () => {
		api.get("/get_current_user").then(json => setUser(json.data));
	};

	const getUserTasks = () => {
		if (type && itemInfo["material"]) {
			api.get(
				`/projects/${projectId}/tasks/object_id/${itemInfo["material"]}/object_type/${type}/user/${user["email"]}`,
			).then(json => {
				setUserTasks(json.data);
			});
		}
	};

	const getOptions = () =>
		workflows
			? workflows.map((w, idx) => ({
					value: idx,
					label: `${w.name} (Number of steps: ${w.steps.length})`,
				}))
			: [];

	const getScenarioOptions = () =>
		scenarios
			? scenarios.map((s, idx) => ({
					value: idx,
					label: `${s.iteration_name}`,
				}))
			: [];

	const handleSaveWithAutomated = values => {
		if (values["min"] > values["max"]) {
			message.error(`ROP value must be less than Max value (Max Value = ${values["max"]}) `);
			return;
		}
		setLoading(true);
		const rop_change = values["min"] - itemInfo.rop;
		const max_change = values["max"] - itemInfo.max;

		const data = {
			rop_change,
			max_change,
			type: itemInfo["Type"],
			criticality: itemInfo["cl_name"],

			workflow_info: {
				object: itemInfo["material"],
				object_type: "material",
				project_id: projectId,
				material_object_id: itemInfo["_id"],

				min: values["min"],
				max: values["max"],
				comment: values["comment"],
			},
		};

		const bodyFormData = new FormData();
		bodyFormData.append("json", JSON.stringify(data));

		api.post(`/tasks/automatic`, bodyFormData, {"Content-Type": "multipart/form-data", omitGlobalError: true})
			.then(json => {
				if (Object.keys(json.data).includes("msg")) {
					message.success(json.data.msg);
					closeModal();
					handleApprove();
				} else {
					setMsg(
						"The system wasn't able to select the workflow automatically. Please choose one in order to proceed",
					);
					setWorkflows(json.data.data);
					setAllowSelect(true);
				}
			})
			.catch(async error => message.error(await extractErrorMessage(error), 10))
			.finally(() => setLoading(false));
	};

	const handleSave = values => {
		if (values["min"] > values["max"]) {
			message.error(`ROP value must be less than Max value (Max Value = ${values["max"]}) `);
			return;
		}
		setLoading(true);
		let req = {};
		if (type === "material") {
			message.info("Start the approving process for the material");
			workflows[values["workflow_idx"]]["steps"][0]["comment"] = values["comment"];
			req = {
				object: itemInfo["material"],
				object_type: "material",
				project_id: projectId,
				material_object_id: itemInfo["_id"],
				steps: workflows[values["workflow_idx"]]["steps"],
				workflow_id: workflows[values["workflow_idx"]]["_id"],
				description: workflows[values["workflow_idx"]]["name"],
				workflow_name: workflows[values["workflow_idx"]]["name"],
				min: values["min"],
				max: values["max"],
				comment: values["comment"],
			};
		}

		if (type === "scope") {
			message.info("Start the approving process for the scope");
			req = {
				object: itemInfo["material"],
				object_type: "scope",
				project_id: projectId,
				material_object_id: itemInfo["_id"],
				steps: workflows[values["workflow_idx"]]["steps"],
				workflow_id: workflows[values["workflow_idx"]]["_id"],
				description: workflows[values["workflow_idx"]]["name"],
				workflow_name: workflows[values["workflow_idx"]]["name"],

				comment: values["comment"],
			};
			let min_max_mode = {};
			switch (scopeMode) {
				case "fixed":
					min_max_mode = {
						type: "fixed",
						min: values["min"],
						max: values["max"],
					};
					break;
			}

			req["mode"] = min_max_mode;
		}

		const bodyFormData = new FormData();
		bodyFormData.append("json", JSON.stringify(req));

		api.post(`/tasks`, bodyFormData, {"Content-Type": "multipart/form-data", omitGlobalError: true})
			.then(json => {
				if (Object.keys(json.data).includes("msg")) {
					message.success(json.data.msg);
					closeModal();
					handleApprove();
				}
			})
			.catch(async error => message.error(await extractErrorMessage(error), 10))
			.finally(() => setLoading(false));
	};

	const onOk = () => {
		form.submit();
	};

	const getCurrentTask = () => tasks.filter(x => x["status"] === "active");

	const getTitle = () => {
		const current = getCurrentTask();
		if (current.length === 0 || type === "scope") {
			return "Status";
		} else {
			return `Status (${ROP_MIN_LABEL} - ${current[0]["min"]}, MAX - ${current[0]["max"]})`;
		}
	};

	const handleApprove = () => {
		getTasks();
		onApprove();
	};

	const onSave = values => {
		if (allowSelect) {
			handleSave(values);
		} else {
			handleSaveWithAutomated(values);
		}
	};

	const [resetForm] = Form.useForm();
	const [reviewForm] = Form.useForm();

	const openResetModal = () => {
		resetForm.resetFields();
		resetForm.setFieldsValue({
			resetApproval: isApproved,
			resetReview: isActivelyReviewed,
		});
		setIsResetModalVisible(true);
	};

	const closeResetModal = () => setIsResetModalVisible(false);

	const onResetOk = () => resetForm.submit();

	const onResetSubmit = values => {
		const requests = [];
		if (values.resetApproval) {
			requests.push(api.get(`/projects/${projectId}/material/${itemInfo.material}/approve/reset`, {omitGlobalError: true}));
		}
		if (canReview && values.resetReview) {
			requests.push(api.get(`/projects/${projectId}/material/${itemInfo.material}/review/reset`, {omitGlobalError: true}));
		}

		if (requests.length === 0) {
			message.info("Nothing was selected to reset.");
			return;
		}

		Promise.allSettled(requests).then(async results => {
			const succeeded = results.filter(result => result.status === "fulfilled");
			const failed = results.filter(result => result.status === "rejected");

			if (succeeded.length > 0) {
				closeResetModal();
				handleApprove();
			}

			if (failed.length === 0) {
				message.success("Successfully reset.");
			} else if (succeeded.length > 0) {
				message.warning("Some resets failed. The page was refreshed with the latest state.");
			} else {
				message.error(await extractErrorMessage(failed[0].reason));
			}
		});
	};

	const openReviewModal = () => {
		reviewForm.resetFields();
		reviewForm.setFieldsValue({review_expiry_date: dayjs().add(6, "month")});
		setIsReviewModalVisible(true);
	};

	const closeReviewModal = () => setIsReviewModalVisible(false);

	const onReviewOk = () => reviewForm.submit();

	const onReviewSubmit = values => {
		api.post(
			`/projects/${projectId}/material/${itemInfo.material}/review`,
			{
				review_expiry_date: values.review_expiry_date.format("YYYY-MM-DD"),
				notes: values.notes,
			},
			{omitGlobalError: true},
		)
			.then(() => {
				message.success("The material was successfully marked as reviewed.");
				closeReviewModal();
				handleApprove();
			})
			.catch(async error => message.error(await extractErrorMessage(error)));
	};

	const isApproved = itemInfo.approved === true || itemInfo.approved === 1;
	const isActivelyReviewed = itemInfo.is_reviewed === true || itemInfo.is_reviewed === 1;
	const canReset = isApproved || (canReview && isActivelyReviewed);
	const hasActiveTasks = tasks && tasks.filter(t => t.status !== "done").length > 0;

	const reviewActionStyle = hasActiveTasks ? {marginLeft: 10, marginBottom: 5} : {margin: 5};

	const reviewActions = (
		<>
			{canEdit && canReset && (
				<Button style={reviewActionStyle} onClick={openResetModal}>
					Reset the review/approval process
				</Button>
			)}
			{canReview && !isActivelyReviewed && (
				<Button style={reviewActionStyle} onClick={openReviewModal}>
					Mark as reviewed
				</Button>
			)}
		</>
	);

	return (
		<DataCard style={{width: "100%", margin: "0 5"}} title={getTitle()}>
			<ModalWindow
				modalProps={{
					maxHeight: "40vh",
					cancelButtonProps: {disabled: loading},
					okButtonProps: {disabled: loading, loading: loading},
				}}
				title={"Assign approval"}
				isModalVisible={isModalVisible}
				onOk={onOk}
				onCancel={closeModal}>
				<Spin spinning={loading}>
					<Form onFinish={onSave} form={form} style={{height: 275}}>
						{msg && (
							<Tag color="error" style={{marginBottom: 10}}>
								{msg}
							</Tag>
						)}
						{allowSelect && (
							<Form.Item
								label={"Select the Workflow"}
								name={"workflow_idx"}
								rules={[{required: true, message: "Please select a workflow"}]}>
								<Select options={getOptions()} />
							</Form.Item>
						)}
						{type === "material" && (
							<Form.Item
								label={`Input the ${ROP_MIN_LABEL}`}
								name={"min"}
								rules={[{required: true, message: `Please input the ${ROP_MIN_LABEL}`}]}>
								<InputNumber />
							</Form.Item>
						)}
						{type === "material" && (
							<Form.Item
								label={"Input the MAX"}
								name={"max"}
								rules={[{required: true, message: "Please input the MAX"}]}>
								<InputNumber />
							</Form.Item>
						)}
						{type === "scope" && (
							<div>
								<Form.Item label={"Select the scenario"} name={"scenario"}>
									<Select options={getScenarioOptions()} />
								</Form.Item>
							</div>
						)}
						<Form.Item label={"Input the comment"} name={"comment"}>
							<TextArea rows={4} />
						</Form.Item>
					</Form>
				</Spin>
			</ModalWindow>
			<ModalWindow
				modalProps={{maxHeight: "30vh"}}
				title={"Reset the review/approval process"}
				isModalVisible={isResetModalVisible}
				onOk={onResetOk}
				onCancel={closeResetModal}>
				<Form onFinish={onResetSubmit} form={resetForm} layout="vertical">
					<p>Select what you want to reset:</p>
					<Form.Item name={"resetApproval"} valuePropName={"checked"}>
						<Checkbox disabled={!isApproved}>Approval</Checkbox>
					</Form.Item>
					{canReview && (
						<Form.Item name={"resetReview"} valuePropName={"checked"}>
							<Checkbox disabled={!isActivelyReviewed}>Review</Checkbox>
						</Form.Item>
					)}
				</Form>
			</ModalWindow>
			<ModalWindow
				modalProps={{maxHeight: "30vh"}}
				title={"Mark as reviewed"}
				isModalVisible={isReviewModalVisible}
				onOk={onReviewOk}
				onCancel={closeReviewModal}>
				<Form onFinish={onReviewSubmit} form={reviewForm} layout="vertical">
					<Form.Item
						label={"Review expiry date"}
						name={"review_expiry_date"}
						rules={[
							{required: true},
							{
								validator: (_, value) =>
									value && value.isBefore(dayjs(), "day")
										? Promise.reject(new Error("Expiry date cannot be in the past"))
										: Promise.resolve(),
							},
						]}>
						<DatePicker style={{width: "100%"}} disabledDate={current => current && current < dayjs().startOf("day")} />
					</Form.Item>
					<Form.Item label={"Notes"} name={"notes"}>
						<TextArea rows={4} />
					</Form.Item>
				</Form>
			</ModalWindow>
			{tasks && !hasActiveTasks && (
				<div>
					<Button style={{margin: 5}} onClick={openModal}>
						Start the approval process
					</Button>
					{reviewActions}
				</div>
			)}
			{hasActiveTasks && (
				<div>
					<div>{reviewActions}</div>
					<TasksViewer
						onApprove={handleApprove}
						current={getCurrentTask()}
						userInfo={user}
						isAdmin={isAdmin}
						type={type}
						projectId={projectId}
						itemInfo={itemInfo}
						userTasks={userTasks}
					/>
				</div>
			)}
		</DataCard>
	);
};
